<template>
    <sidebar @close="$emit('close')" v-if="bankAccount">
        <template v-slot:title>
            {{ bankAccount.name }}
        </template>

        <template v-slot:header>
            <div class="row mb-1">
                <div class="col-12">
                    <span class="text-muted">{{ company.name }} &bullet; {{ bankAccount.bank_name }}</span>
                </div>

                <div class="col-12">
                    <span class="text-muted">{{ bankAccount.public_id }} &nbsp;<a href @click.prevent="$bus.copyToClipboard(bankAccount.public_id)"><i class="far fa-copy"></i></a></span>
                </div>
            </div>

            <!-- Tabs -->
            <div class="nav-tabs-container">
                <div class="nav-tabs-scroll-helper">
                    <ul class="nav nav-tabs">
                        <li class="nav-item">
                            <a class="nav-link" :class="{active: activeTab == 'main'}" href="#tabEarningContentMain" id="tabEarningLinkMain" data-toggle="tab" role="tab" aria-controls="tabEarningContentMain" :aria-selected="activeTab == 'main'">Main</a>
                        </li>
                    </ul>
                </div>
            </div>
        </template>

        <slot>
            <div class="tab-content">
                <div class="tab-pane" :class="{'show active': activeTab == 'main'}" id="tabEarningContentMain" role="tabpanel" aria-labelledby="tabEarningLinkMain">

                    <info-widget :collapsible="false">
                        <template v-slot:title>Bank Account Information</template>

                        <template v-slot:headerbutton>
                            <button type="button" class="btn btn-outline-primary" @click.prevent="editModalOpen = true">
                                <i class="far fa-pen" aria-hidden="true" title="Edit"></i>
                                Edit
                            </button>
                        </template>

                        <slot>
                            <div class="card-body py-1 mt-1">
                                <div class="row mb-0">
                                    <div class="col-12">
                                        <div class="mui-textfield">
                                            <span class="textfield">{{ bankAccount.name_on_account }}</span>
                                            <label>Name on the Account</label>
                                        </div>
                                    </div>

                                    <div class="col-6">
                                        <div class="mui-textfield">
                                            <span class="textfield">{{ bankAccount.routing_number }}</span>
                                            <label>Routing Number</label>
                                        </div>
                                    </div>

                                    <div class="col-6">
                                        <div class="mui-textfield">
                                            <span class="textfield">x{{ bankAccount.account_number_last_4 }}</span>
                                            <label>Account Number</label>
                                        </div>
                                    </div>

                                    <div class="col-12">
                                        <div class="mui-textfield">
                                            <span class="textfield">{{ bankAccount.check_number }}</span>
                                            <label>Starting Check #</label>
                                        </div>
                                    </div>

                                    <div class="col-12">
                                        <div class="mui-textfield">
                                            <span class="textfield">
                                                {{ bankAccount.address_1 }}<br />
                                                {{ bankAccount.address_2 }}<br v-if="bankAccount.address_2" />
                                                {{ bankAccount.city }}, {{ bankAccount.state }} {{ bankAccount.postal_code }}
                                            </span>
                                            <label>Address</label>
                                        </div>

                                        <div class="mui-textfield">
                                            <span class="textfield">{{ usageDefaults }}</span>
                                            <label>Default</label>
                                        </div>
                                    </div>

                                </div>

                                <p class="mt-2 text-center">
                                    <button class="btn btn-link" @click.prevent="setActiveStatus(!bankAccount.is_active)">{{ bankAccount.is_active ? 'Deactivate' : 'Reactivate' }} Account</button>
                                </p>
                            </div>
                        </slot>
                    </info-widget>

                    <positions-widget
                        :bank-account="bankAccount"
                        :start-expanded="true"
                    />

                </div>
            </div>
        </slot>

        <edit-modal
            v-if="editModalOpen"
            :bank-account="bankAccount"
            :client="client"
            @updated="onUpdate"
            @close="editModalOpen = false"
        />

    </sidebar>
</template>

<script>
import Sidebar from '@/components/Sidebar'
import InfoWidget from '@/components/InfoWidget'
import ButtonRow from '@/components/ButtonRow'
import EditModal from './EditModal'
import PositionsWidget from './PositionsWidget'

export default {
    props: ['bankAccountId', 'client', 'tab', ],
    components: {Sidebar, InfoWidget, ButtonRow, EditModal, PositionsWidget, },
    computed: {
        company() {
            return this.client.companies.find(c => c.id == this.bankAccount.company_id)
        },
        usageDefaults() {
            if (!this.bankAccount) {
                return ''
            }

            const result = []
            if (this.bankAccount.is_default_billing) {
                result.push('Billing')
            }

            if (this.bankAccount.is_default_net_pay) {
                result.push('Net Pay')
            }

            if (this.bankAccount.is_default_taxes) {
                result.push('Taxes')
            }

            if (result.length < 1) {
                return 'None'
            }

            return result.join(', ')
        },
    },
    data() {
        return {
            bankAccount: null,
            activeTab: this.tab || 'main',
            editModalOpen: false,
        }
    },
    watch: {
        bankAccountId() {
            this.getData()
        }
    },
    mounted() {
        this.getData()
    },
    methods: {
        getData() {
            this.$store.dispatch('START_LOADING')
            this.$api.get(`/clients/${this.client.id}/settings/bank-accounts/${this.bankAccountId}`).then(resp => {
                this.bankAccount = resp
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
        onUpdate(obj) {
            if (obj) {
                Object.assign(this.bankAccount, obj)
                this.$emit('updated')
            }
            else {
                this.getData()
            }
        },
        setActiveStatus(isActive) {
            if (!confirm('Are you sure?')) {return}
            this.$store.dispatch('START_LOADING')
            this.$api.patch(`/clients/${this.client.id}/settings/bank-accounts/${this.bankAccountId}`, {is_active: isActive}).then(resp => {
                this.bankAccount = resp
                this.$emit('updated')
                this.$store.dispatch('STOP_LOADING')
            }).catch((errors) => {
                this.$store.dispatch('STOP_LOADING')
                this.$bus.showError(errors.__all__)
            })
        },
    },
}
</script>

